import { useEffect, useState } from 'react';
import { useRootElement } from '../../context/root-element-context';
import { getEnv } from '../../utils/env';

const baseUrl = getEnv('CUSTOM_STYLES_BUCKET_URL');

type UseStoreCustomStylesProps = {
  enabled: boolean;
  storeId: string | null | undefined;
};

type StylesState = {
  loading: boolean;
  applied: boolean;
  error: boolean;
};

export const useStoreCustomStyles = ({
  enabled,
  storeId,
}: UseStoreCustomStylesProps): StylesState => {
  const rootElement = useRootElement();
  const [state, setState] = useState<StylesState>({
    loading: true,
    applied: false,
    error: false,
  });

  useEffect(() => {
    if (!storeId) return;

    // Case 1: Has storeId but is not enabled
    if (!enabled) {
      setState({ loading: false, applied: false, error: false });
      return;
    }

    const getStylesheetUrl = (storeId: string): string | null => {
      if (!storeId) return null;
      return `${baseUrl}/${storeId}.css`;
    };

    const createStyles = (url: string): HTMLLinkElement => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;

      return link;
    };

    const stylesheetUrl = getStylesheetUrl(storeId);
    // Case 2: Has storeId, is enabled, but styles don't exist
    if (!stylesheetUrl) {
      setState({ loading: false, applied: false, error: true });
      return;
    }

    const linkElement = createStyles(stylesheetUrl);

    // Handle successful load
    linkElement.onload = () => {
      // Case 3: Has storeId, is enabled, and styles were applied
      setState({ loading: false, applied: true, error: false });
    };

    linkElement.onerror = () => {
      // Case 4: Has storeId, is enabled, but styles failed to load
      console.warn('Failed to load custom stylesheet');
      setState({ loading: false, applied: false, error: true });
    };

    // In production the widget runs inside a shadow root; styles must be
    // appended there to pierce the shadow boundary. In dev (classic mount)
    // getRootNode() returns the Document, so we fall back to document.head.
    const rootNode = rootElement?.getRootNode();
    const container = rootNode instanceof ShadowRoot ? rootNode : document.head;
    container.appendChild(linkElement);

    return () => {
      container.removeChild(linkElement);
      setState({ loading: false, applied: false, error: false });
    };
  }, [enabled, storeId, rootElement]);

  return state;
};
